import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, Shuffle, CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nurseryId: string;
  classes: { id: string; name: string }[];
  onSuccess: () => void;
}

const subjects = [
  { value: "عربي", label: "اللغة العربية" },
  { value: "رياضيات", label: "الرياضيات" },
  { value: "انجليزي", label: "اللغة الإنجليزية" },
  { value: "علوم", label: "العلوم" },
  { value: "اسلامي", label: "التربية الإسلامية" },
];

const stages = [
  { value: "kg1", label: "KG1" },
  { value: "kg2", label: "KG2" },
  { value: "grade1", label: "أولى ابتدائي" },
  { value: "grade2", label: "ثانية ابتدائي" },
  { value: "grade3", label: "ثالثة ابتدائي" },
];

const typeLabels: Record<string, string> = { multiple_choice: "اختياري", true_false: "صح/خطأ", essay: "مقالي" };
const diffLabels: Record<string, string> = { easy: "سهل", medium: "متوسط", hard: "صعب" };
const diffColors: Record<string, string> = {
  easy: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  hard: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

export default function AutoExamDialog({ open, onOpenChange, nurseryId, classes, onSuccess }: Props) {
  const { toast } = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [saving, setSaving] = useState(false);

  // Step 1: Filters
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [diffFilter, setDiffFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [questionCount, setQuestionCount] = useState("10");

  // Step 2: Review & settings
  const [examTitle, setExamTitle] = useState("");
  const [classId, setClassId] = useState("");
  const [duration, setDuration] = useState("15");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: allQuestions } = useQuery({
    queryKey: ["bank-questions-auto", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("questions")
        .select("*")
        .eq("nursery_id", nurseryId)
        .order("created_at", { ascending: false });
      return data || [];
    },
    enabled: open && !!nurseryId,
  });

  const filteredPool = useMemo(() => {
    return (allQuestions || []).filter((q: any) => {
      if (subjectFilter !== "all" && q.subject !== subjectFilter) return false;
      if (stageFilter !== "all" && q.stage !== stageFilter) return false;
      if (diffFilter !== "all" && q.difficulty !== diffFilter) return false;
      if (typeFilter !== "all" && q.question_type !== typeFilter) return false;
      return true;
    });
  }, [allQuestions, subjectFilter, stageFilter, diffFilter, typeFilter]);

  const autoSelect = () => {
    const count = Math.min(parseInt(questionCount) || 10, filteredPool.length);
    const shuffled = [...filteredPool].sort(() => Math.random() - 0.5);
    setSelectedIds(new Set(shuffled.slice(0, count).map((q: any) => q.id)));
    setExamTitle(
      subjectFilter !== "all"
        ? `امتحان ${subjects.find((s) => s.value === subjectFilter)?.label || subjectFilter}`
        : "امتحان شامل"
    );
    setStep(2);
  };

  const toggleQuestion = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    if (selectedIds.size === 0 || !examTitle.trim()) return;
    setSaving(true);
    try {
      const { data: exam, error } = await supabase.from("exams").insert({
        title: examTitle.trim(),
        nursery_id: nurseryId,
        class_id: classId || null,
        duration_minutes: parseInt(duration) || 15,
        max_score: selectedIds.size,
        is_interactive: true,
      }).select("id").single();
      if (error) throw error;

      const examQuestions = Array.from(selectedIds).map((qId, i) => ({
        exam_id: exam.id,
        question_id: qId,
        sort_order: i,
        points: 1,
      }));
      const { error: linkError } = await supabase.from("exam_questions").insert(examQuestions);
      if (linkError) throw linkError;

      toast({ title: `🎉 امتحانك جاهز 👏 — ${selectedIds.size} سؤال` });
      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSubjectFilter("all");
    setStageFilter("all");
    setDiffFilter("all");
    setTypeFilter("all");
    setQuestionCount("10");
    setExamTitle("");
    setClassId("");
    setDuration("15");
    setSelectedIds(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetForm(); }}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            {step === 1 ? "📝 إنشاء امتحان تلقائي" : "مراجعة الامتحان"}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            <div className="bg-gradient-to-l from-primary/10 to-primary/5 rounded-xl p-4 text-center space-y-1">
              <div className="text-3xl">📚✨</div>
              <p className="font-bold text-sm text-foreground">اختر المادة والمرحلة وعدد الأسئلة</p>
              <p className="text-xs text-muted-foreground">النظام يسحب الأسئلة من البنك ويكوّن امتحان أوتوماتيك</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>المادة</Label>
                <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع المواد</SelectItem>
                    {subjects.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>المرحلة</Label>
                <Select value={stageFilter} onValueChange={setStageFilter}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع المراحل</SelectItem>
                    {stages.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>مستوى الصعوبة</Label>
                <Select value={diffFilter} onValueChange={setDiffFilter}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">الكل</SelectItem>
                    <SelectItem value="easy">🟢 سهل</SelectItem>
                    <SelectItem value="medium">🟡 متوسط</SelectItem>
                    <SelectItem value="hard">🔴 صعب</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>نوع الأسئلة</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">الكل</SelectItem>
                    <SelectItem value="multiple_choice">اختيار من متعدد</SelectItem>
                    <SelectItem value="true_false">صح وخطأ</SelectItem>
                    <SelectItem value="essay">مقالي</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>عدد الأسئلة</Label>
              <Select value={questionCount} onValueChange={setQuestionCount}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["5", "10", "15", "20", "25", "30"].map((n) => (
                    <SelectItem key={n} value={n}>{n} سؤال</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-muted/50 rounded-lg p-3 text-center">
              <p className="text-sm">
                📊 عدد الأسئلة المتاحة:{" "}
                <span className="font-bold text-primary">{filteredPool.length}</span>
              </p>
            </div>

            <Button
              onClick={autoSelect}
              disabled={filteredPool.length === 0}
              className="w-full h-12 text-base rounded-xl"
            >
              <Shuffle className="w-5 h-5 ml-2" />
              🚀 إنشاء الامتحان ({Math.min(parseInt(questionCount) || 10, filteredPool.length)} سؤال)
            </Button>

            {filteredPool.length === 0 && (
              <p className="text-xs text-center text-muted-foreground">لا توجد أسئلة بهذه المواصفات. جرب تعديل الفلاتر أو أضف أسئلة جديدة.</p>
            )}
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0 space-y-3">
            {/* Exam settings */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">عنوان الامتحان</Label>
                <Input
                  value={examTitle}
                  onChange={(e) => setExamTitle(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">المدة</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["5", "10", "15", "20", "30", "45"].map((d) => (
                      <SelectItem key={d} value={d}>{d} د</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {classes.length > 0 && (
              <div>
                <Label className="text-xs">الفصل</Label>
                <Select value={classId} onValueChange={setClassId}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="اختر الفصل" /></SelectTrigger>
                  <SelectContent>
                    {classes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              ✏️ راجع الأسئلة المختارة ({selectedIds.size}) — يمكنك إزالة أو إضافة أسئلة:
            </p>

            <ScrollArea className="flex-1 max-h-[35vh]">
              <div className="space-y-2 pl-2">
                {filteredPool.map((q: any) => (
                  <div
                    key={q.id}
                    className={`border rounded-lg p-2.5 cursor-pointer transition-colors text-sm ${
                      selectedIds.has(q.id) ? "border-primary bg-primary/5" : "opacity-50 hover:opacity-100"
                    }`}
                    onClick={() => toggleQuestion(q.id)}
                  >
                    <div className="flex items-start gap-2">
                      <Checkbox checked={selectedIds.has(q.id)} className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">{q.question_text}</p>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-[10px]">
                            {typeLabels[q.question_type] || q.question_type}
                          </Badge>
                          <Badge className={`text-[10px] ${diffColors[q.difficulty] || ""}`}>
                            {diffLabels[q.difficulty] || q.difficulty}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                رجوع
              </Button>
              <Button
                onClick={handleCreate}
                disabled={saving || selectedIds.size === 0 || !examTitle.trim()}
                className="flex-1"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin ml-2" />جاري الإنشاء...</>
                ) : (
                  `إنشاء الامتحان (${selectedIds.size} سؤال)`
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
